// Role-based access control utilities

export const ROLES = {
  AGENT: 'agent',
  TEAM_LEAD: 'team_lead',
  ADMIN: 'admin'
};

// Check if user has access to user management
export const canAccessUserManagement = (userRole) => {
  return userRole === ROLES.ADMIN;
};

// Check if user has access to analytics
export const canAccessAnalytics = (userRole) => {
  return [ROLES.TEAM_LEAD, ROLES.ADMIN].includes(userRole);
};

// Check if user has access to activity/call logs
export const canAccessActivity = (userRole) => {
  return [ROLES.AGENT, ROLES.TEAM_LEAD, ROLES.ADMIN].includes(userRole);
};

// Check if user should see team-filtered data
export const shouldFilterByTeam = (userRole) => {
  return userRole === ROLES.TEAM_LEAD;
};

// Check if user can see all teams data
export const canSeeAllTeams = (userRole) => {
  return userRole === ROLES.ADMIN;
};

// Filter users based on current user's role and team
export const filterUsersByRole = (allUsers, currentUser) => {
  if (!currentUser) return [];
  
  // Admins can see all users
  if (currentUser.role === ROLES.ADMIN) {
    return allUsers;
  }
  
  // Team leads can see users in any of their teams
  if (currentUser.role === ROLES.TEAM_LEAD) {
    const currentUserTeamIds = getUserTeamIds(currentUser);
    
    if (currentUserTeamIds.length === 0) {
      return []; // Team lead with no teams sees no users
    }
    
    return allUsers.filter(user => {
      const userTeamIds = getUserTeamIds(user);
      // Check if any of the user's teams match any of the team lead's teams
      return userTeamIds.some(teamId => currentUserTeamIds.includes(teamId));
    });
  }
  
  // Agents can only see themselves
  if (currentUser.role === ROLES.AGENT) {
    return allUsers.filter(user => user.id === currentUser.id);
  }
  
  return [];
};

// Filter teams based on current user's role
export const filterTeamsByRole = (allTeams, currentUser) => {
  if (!currentUser) return [];
  
  // Admins can see all teams
  if (currentUser.role === ROLES.ADMIN) {
    return allTeams;
  }
  
  // Team leads can see all their assigned teams
  if (currentUser.role === ROLES.TEAM_LEAD) {
    const userTeamIds = getUserTeamIds(currentUser);
    
    if (userTeamIds.length === 0) {
      return []; // Team lead with no teams sees no teams
    }
    
    return allTeams.filter(team => userTeamIds.includes(team.id));
  }
  
  // Agents don't typically need team filtering in UI
  return [];
};

// Get role display name
export const getRoleDisplayName = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Admin';
    case ROLES.TEAM_LEAD:
      return 'Team Lead';
    case ROLES.AGENT:
      return 'Agent';
    default:
      return 'Unknown';
  }
};

// Check if current user can manage/edit another user
export const canManageUser = (currentUser, targetUser) => {
  if (!currentUser || !targetUser) return false;
  
  // Admins can manage anyone
  if (currentUser.role === ROLES.ADMIN) {
    return true;
  }
  
  // Team leads can manage agents in any of their teams
  if (currentUser.role === ROLES.TEAM_LEAD && targetUser.role === ROLES.AGENT) {
    const teamLeadTeamIds = getUserTeamIds(currentUser);
    const targetUserTeamIds = getUserTeamIds(targetUser);
    
    if (teamLeadTeamIds.length === 0) {
      return false; // Team lead with no teams can't manage anyone
    }
    
    // Check if target user belongs to any of the team lead's teams
    return targetUserTeamIds.some(teamId => teamLeadTeamIds.includes(teamId));
  }
  
  // Agents can only manage themselves
  if (currentUser.role === ROLES.AGENT) {
    return currentUser.id === targetUser.id;
  }
  
  return false;
};

// Helper function to get all team IDs for a user
// Uses team_ids array field
// Ensures all IDs are converted to numbers for consistent comparison
export const getUserTeamIds = (user) => {
  if (!user) return [];
  
  // Dashboard users: Use team_ids array (primary field)
  if (user.team_ids && Array.isArray(user.team_ids) && user.team_ids.length > 0) {
    // Convert all values to numbers to handle cases where they might be strings
    return user.team_ids.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
  }
  
  // Imported users: Fall back to single team_id
  if (user.team_id) {
    const teamId = typeof user.team_id === 'string' ? parseInt(user.team_id, 10) : user.team_id;
    return isNaN(teamId) ? [] : [teamId];
  }
  
  return [];
};
